import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  UsersIcon,
  EnvelopeIcon,
  NewspaperIcon,
  HomeIcon,
  CheckIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  CalendarIcon,
  BanknotesIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';

export default function PermissionsModal({ admin, onClose, onSave }) {
  const [permissions, setPermissions] = useState(null);
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer la structure des permissions
      const structureRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/permissions/structure`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const struct = structureRes.data;
      setStructure(struct);

      const mergeWithStructure = (permDoc) => {
        const out = { ...(permDoc || {}) };
        Object.keys(struct).forEach((module) => {
          out[module] = { ...(out[module] || {}) };
          struct[module].permissions.forEach((perm) => {
            if (out[module][perm.key] === undefined) {
              out[module][perm.key] = false;
            }
          });
        });
        return out;
      };

      // Récupérer les permissions actuelles de l'admin
      try {
        const permRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/permissions/${admin._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPermissions(mergeWithStructure(permRes.data));
      } catch (error) {
        const emptyPermissions = {};
        Object.keys(struct).forEach((module) => {
          emptyPermissions[module] = {};
          struct[module].permissions.forEach((perm) => {
            emptyPermissions[module][perm.key] = false;
          });
        });
        setPermissions(emptyPermissions);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      alert('Erreur lors du chargement des permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (module, permissionKey) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permissionKey]: !prev[module][permissionKey],
      },
    }));
  };

  const handleToggleModule = (module) => {
    const allEnabled = structure[module].permissions.every(
      perm => permissions[module][perm.key]
    );
    
    const newModulePermissions = {};
    structure[module].permissions.forEach(perm => {
      newModulePermissions[perm.key] = !allEnabled;
    });

    setPermissions(prev => ({
      ...prev,
      [module]: newModulePermissions,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      Object.keys(structure).forEach((moduleKey) => {
        payload[moduleKey] = permissions[moduleKey] || {};
      });
      await axios.put(
        `${process.env.REACT_APP_API_URL}/permissions/${admin._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Permissions mises à jour avec succès');
      onSave();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des permissions');
    } finally {
      setSaving(false);
    }
  };

  const getModuleIcon = (iconName) => {
    const icons = {
      UsersIcon,
      EnvelopeIcon,
      NewspaperIcon,
      HomeIcon,
      BuildingLibraryIcon,
      CalendarDaysIcon,
      CalendarIcon,
      BanknotesIcon,
      DocumentTextIcon,
      BuildingStorefrontIcon,
    };
    return icons[iconName] || HomeIcon;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Gestion des permissions
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {admin.prenom} {admin.nom} - {admin.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {structure && permissions && Object.keys(structure).map(moduleKey => {
              const module = structure[moduleKey];
              const Icon = getModuleIcon(module.icon);
              const allEnabled = module.permissions.every(
                perm => permissions[moduleKey]?.[perm.key]
              );
              const someEnabled = module.permissions.some(
                perm => permissions[moduleKey]?.[perm.key]
              );

              return (
                <div key={moduleKey} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Module Header */}
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5 text-gray-600" />
                      <h3 className="font-medium text-gray-900">{module.label}</h3>
                    </div>
                    <button
                      onClick={() => handleToggleModule(moduleKey)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        allEnabled
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : someEnabled
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {allEnabled ? 'Tout désactiver' : 'Tout activer'}
                    </button>
                  </div>

                  {/* Permissions List */}
                  <div className="divide-y divide-gray-200">
                    {module.permissions.map(permission => {
                      const isEnabled = permissions[moduleKey]?.[permission.key];
                      
                      return (
                        <div
                          key={permission.key}
                          className="px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <label className="flex items-start cursor-pointer">
                            <div className="flex items-center h-5">
                              <input
                                type="checkbox"
                                checked={isEnabled || false}
                                onChange={() => handleTogglePermission(moduleKey, permission.key)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {permission.label}
                                </span>
                                {isEnabled && (
                                  <CheckIcon className="h-4 w-4 text-green-500 ml-2" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                {permission.description}
                              </p>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
